/**
 * Vue <Transition> for the Blade pages, with Vue 3's timing (runtime-dom Transition.ts):
 *  - enter: the element is shown with {name}-enter-from + {name}-enter-active, the next frame
 *    (double requestAnimationFrame) swaps {name}-enter-from for {name}-enter-to;
 *  - leave: {name}-leave-from + {name}-leave-active, the next frame swaps {name}-leave-from for
 *    {name}-leave-to;
 *  - done like Vue's whenTransitionEnds: the longest of the element's transitions or animations
 *    (getTransitionInfo) decides the type; done when `propCount` transitionend/animationend
 *    events of the element arrived, or after duration + delay + 1 ms. At once (no timer) when
 *    there is no transition (parity STABILIZE_CSS).
 * `name` defaults to « v » (app.scss classes in components/_transition.scss). An `appear`
 * transition is an enter run when the element is first rendered.
 * mode="out-in" = `await leave(old)`, then `enter(new)`.
 */

export interface EnterOptions {
    /** enter-active-class (Simulator.vue: "v-enter-active-delayed"). */
    activeClass?: string;
    /** <Transition name>, default "v". */
    name?: string;
}

export interface LeaveOptions {
    /** <Transition name>, default "v". */
    name?: string;
}

function nextFrame(callback: () => void): void {
    requestAnimationFrame(() => requestAnimationFrame(callback));
}

/** Vue toMs: "0.5s" → 500 (Number(s.slice(0, -1).replace(",", ".")) * 1000). */
function toMs(value: string): number {
    if (value === "auto") return 0;
    return Number(value.slice(0, -1).replace(",", ".")) * 1000;
}

/** Vue getTimeout: max over the durations of duration + its (cycled) delay. */
function getTimeout(delays: string[], durations: string[]): number {
    while (delays.length < durations.length) {
        delays = delays.concat(delays);
    }
    return Math.max(...durations.map((d, i) => toMs(d) + toMs(delays[i])));
}

type TransitionInfo = { type: "transition" | "animation" | null; timeout: number; propCount: number };

/** Vue getTransitionInfo (no expected type). */
function getTransitionInfo(el: Element): TransitionInfo {
    const styles = window.getComputedStyle(el);
    const get = (key: string) => (styles.getPropertyValue(key) || "").split(", ");
    const transitionDelays = get("transition-delay");
    const transitionDurations = get("transition-duration");
    const transitionTimeout = getTimeout(transitionDelays, transitionDurations);
    const animationDelays = get("animation-delay");
    const animationDurations = get("animation-duration");
    const animationTimeout = getTimeout(animationDelays, animationDurations);
    const timeout = Math.max(transitionTimeout, animationTimeout);
    const type = timeout > 0 ? (transitionTimeout > animationTimeout ? "transition" : "animation") : null;
    const propCount = type ? (type === "transition" ? transitionDurations.length : animationDurations.length) : 0;
    return { type, timeout, propCount };
}

/** Per element state of Vue's runtime-dom (_endId, _isLeaving, _enterCancelled, enterCb). */
interface TransitionState {
    endId?: number;
    isLeaving?: boolean;
    enterCancelled?: boolean;
    enterCb?: (cancelled: boolean) => void;
}
const states = new WeakMap<Element, TransitionState>();
const stateOf = (el: Element): TransitionState => {
    let state = states.get(el);
    if (!state) {
        state = {};
        states.set(el, state);
    }
    return state;
};
let endId = 0;

function whenTransitionEnds(el: Element, resolve: () => void): void {
    const id = (stateOf(el).endId = ++endId);
    const resolveIfNotStale = (): void => {
        if (id === stateOf(el).endId) resolve();
    };
    const { type, timeout, propCount } = getTransitionInfo(el);
    if (!type) {
        resolve();
        return;
    }
    const endEvent = `${type}end`;
    let ended = 0;
    const end = (): void => {
        el.removeEventListener(endEvent, onEnd);
        resolveIfNotStale();
    };
    const onEnd = (event: Event): void => {
        if (event.target === el && ++ended >= propCount) end();
    };
    window.setTimeout(() => {
        if (ended < propCount) end();
    }, timeout + 1);
    el.addEventListener(endEvent, onEnd);
}

/** Enter of an element just inserted (or just shown) in the page (also an `appear`). */
export function enter(el: Element, options: EnterOptions = {}): Promise<void> {
    const name = options.name ?? "v";
    const active = options.activeClass ?? `${name}-enter-active`;
    const state = stateOf(el);
    el.classList.add(`${name}-enter-from`, active);
    return new Promise((resolve) => {
        let called = false;
        const finishEnter = (cancelled: boolean): void => {
            if (called) return;
            called = true;
            state.enterCancelled = cancelled;
            el.classList.remove(`${name}-enter-to`, active);
            state.enterCb = undefined;
            resolve();
        };
        state.enterCb = finishEnter;
        nextFrame(() => {
            el.classList.remove(`${name}-enter-from`);
            el.classList.add(`${name}-enter-to`);
            whenTransitionEnds(el, () => finishEnter(false));
        });
    });
}

/**
 * Leave of an element: the caller removes (or swaps) it once the promise resolves. An enter
 * still running on the element is cancelled first (BaseTransition leave).
 */
export function leave(el: Element, options: LeaveOptions = {}): Promise<void> {
    const name = options.name ?? "v";
    const state = stateOf(el);
    state.enterCb?.(true);
    state.isLeaving = true;
    el.classList.add(`${name}-leave-from`);
    // Vue forceReflow: the leave starts from the current opacity
    if (!state.enterCancelled) {
        void (el as HTMLElement).offsetHeight;
        el.classList.add(`${name}-leave-active`);
    } else {
        el.classList.add(`${name}-leave-active`);
        void (el as HTMLElement).offsetHeight;
    }
    return new Promise((resolve) => {
        nextFrame(() => {
            if (!state.isLeaving) return;
            el.classList.remove(`${name}-leave-from`);
            el.classList.add(`${name}-leave-to`);
            whenTransitionEnds(el, () => {
                state.isLeaving = false;
                el.classList.remove(`${name}-leave-from`, `${name}-leave-to`, `${name}-leave-active`);
                resolve();
            });
        });
    });
}
